import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Search, Users, Mail, CheckCircle, UserCheck, UserX, ArrowUpDown, Archive, ArchiveRestore, Clock, XCircle, RefreshCw, Trash2, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatPhoneNumber, formatPhoneInput } from '../utils/phoneFormat';
import { formatDate } from '../utils/dateFormat';
import { useAdminSettings } from '../hooks/useAdminSettings';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

function StaffDashboard({ user }) {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [caseSortOrder, setCaseSortOrder] = useState('name_asc');
  const [showArchivedCases, setShowArchivedCases] = useState(false);
  const [newCase, setNewCase] = useState({
    case_name: '',
    case_id_custom: '',
    case_type: 'personal_injury',
    current_status: 'case_initiation',
    client_name: '',
    client_email: '',
    client_phone: '',
    milestones: []
  });

  // Fetch dynamic statuses from admin settings
  const { getStatusOptions, getMilestoneOptions, loading: settingsLoading } = useAdminSettings();
  const caseStatusOptions = getStatusOptions();
  const milestoneOptions = getMilestoneOptions();
  
  const [creatingCase, setCreatingCase] = useState(false);
  
  // State for collapsible invitation groups
  const [expandedInvitationGroups, setExpandedInvitationGroups] = useState({
    pending: true,
    accepted: true,
    expired: true
  });

  const toggleInvitationGroup = (group) => {
    setExpandedInvitationGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  // Invitation status helpers
  const getInvitationStatus = (invitation) => {
    if (invitation.used) {
      return { label: 'Accepted', color: 'bg-green-100 text-green-800', icon: CheckCircle, key: 'accepted' };
    }
    const now = new Date();
    const expiresAt = new Date(invitation.expires_at);
    if (expiresAt < now) {
      return { label: 'Expired', color: 'bg-red-100 text-red-800', icon: XCircle, key: 'expired' };
    }
    return { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock, key: 'pending' };
  };

  // State for invitation search
  const [invitationSearchQuery, setInvitationSearchQuery] = useState('');
  const [showArchivedInvitations, setShowArchivedInvitations] = useState(false);
  
  // Filter invitations based on search query
  const filterInvitations = (invitationList) => {
    if (!invitationSearchQuery.trim()) return invitationList;
    const query = invitationSearchQuery.toLowerCase();
    return invitationList.filter(inv => 
      inv.email?.toLowerCase().includes(query) ||
      inv.invitee_name?.toLowerCase().includes(query) ||
      inv.invitation_code?.toLowerCase().includes(query) ||
      inv.role?.toLowerCase().includes(query)
    );
  };
  
  // Filter by archived status first
  const activeInvitations = invitations.filter(inv => !inv.archived);
  const archivedInvitations = invitations.filter(inv => inv.archived);
  
  // Get invitations based on archived toggle
  const displayInvitations = showArchivedInvitations ? archivedInvitations : activeInvitations;

  const getPendingInvitations = () => filterInvitations(displayInvitations.filter(inv => {
    if (inv.used) return false;
    const expiresAt = new Date(inv.expires_at);
    return expiresAt >= new Date();
  }));

  const getAcceptedInvitations = () => filterInvitations(displayInvitations.filter(inv => inv.used));

  const getExpiredInvitations = () => filterInvitations(displayInvitations.filter(inv => {
    if (inv.used) return false;
    const expiresAt = new Date(inv.expires_at);
    return expiresAt < new Date();
  }));

  const copyInvitationCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Invitation code copied to clipboard');
  };

  const handleResendInvitation = async (invitationCode, email) => {
    try {
      const response = await fetch(`${API}/invitations/${invitationCode}/resend`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json().catch(() => ({}));
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend invitation');
      }
      
      toast.success(`Invitation resent to ${email}`);
      if (data.new_code) {
        fetchData();
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCancelInvitation = async (invitationCode, email) => {
    try {
      const response = await fetch(`${API}/invitations/${invitationCode}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel invitation');
      }
      
      toast.success(`Invitation to ${email} cancelled`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [casesRes, clientsRes, invitationsRes] = await Promise.all([
        fetch(`${API}/cases`, { credentials: 'include' }),
        fetch(`${API}/users/clients`, { credentials: 'include' }),
        fetch(`${API}/invitations`, { credentials: 'include' })
      ]);

      if (casesRes.ok) setCases(await casesRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (invitationsRes.ok) setInvitations(await invitationsRes.json());
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    
    if (!newCase.case_name || !newCase.case_id_custom || !newCase.current_status || !newCase.client_name || !newCase.client_email || !newCase.client_phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreatingCase(true);
    try {
      const response = await fetch(`${API}/cases/with-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          case_name: newCase.case_name,
          case_id_custom: newCase.case_id_custom,
          case_type: newCase.case_type,
          current_status: newCase.current_status,
          client_name: newCase.client_name,
          client_email: newCase.client_email,
          client_phone: newCase.client_phone,
          milestones: newCase.milestones.length > 0 ? newCase.milestones : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create case');
      }
      
      const result = await response.json();
      if (result.client_exists) {
        if (result.email_delivered === false) {
          toast.error(`Case created for existing client, BUT email notification FAILED: ${result.email_error || 'unknown error'}. Please contact them directly.`, { duration: 10000 });
        } else {
          toast.success(`Case created for existing client! They can view it in their portal.`);
        }
      } else {
        if (result.email_delivered === false) {
          toast.error(`Case created, BUT invitation email FAILED: ${result.email_error || 'unknown error'}. Share this code manually: ${result.invitation_code}`, { duration: 15000 });
        } else {
          toast.success(`Case created! Client invitation code: ${result.invitation_code}`);
        }
      }
      setShowCaseModal(false);
      setNewCase({ case_name: '', case_id_custom: '', case_type: 'personal_injury', current_status: 'case_initiation', client_name: '', client_email: '', client_phone: '', milestones: [] });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreatingCase(false);
    }
  };

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/login');
  };

  const handleArchiveCase = async (caseId, caseName, isArchived) => {
    try {
      const response = await fetch(`${API}/cases/${caseId}/archive`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to archive case');
      }
      
      toast.success(isArchived ? `"${caseName}" has been unarchived` : `"${caseName}" has been archived`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Filter cases based on search query
  const filteredCases = cases.filter(caseItem => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      caseItem.case_name?.toLowerCase().includes(query) ||
      caseItem.case_number?.toLowerCase().includes(query) ||
      caseItem.client_name?.toLowerCase().includes(query) ||
      caseItem.case_type?.toLowerCase().includes(query) ||
      caseItem.current_status?.toLowerCase().replace(/_/g, ' ').includes(query)
    );
  });

  // Invitation Card component for reuse in groups
  const InvitationCard = ({ invitation }) => {
    return (
      <div
        className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-[#80298F]/30 transition-colors overflow-hidden"
        data-testid={`invitation-${invitation.invitation_code}`}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <div className="min-w-0">
                  {invitation.invitee_name && (
                    <span className="font-semibold text-gray-900 text-sm sm:text-base mr-1">{invitation.invitee_name}</span>
                  )}
                  <span className={`text-xs sm:text-sm truncate ${invitation.invitee_name ? 'text-gray-600' : 'font-medium text-gray-900'}`}>
                    {invitation.invitee_name ? `(${invitation.email})` : invitation.email}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  invitation.role === 'client' 
                    ? 'bg-[#80298F]/10 text-[#80298F]' 
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {invitation.role}
                </span>
              </div>
            </div>
            {!invitation.used && (
              <div className="flex gap-1 flex-shrink-0 self-end sm:self-start">
                <button
                  onClick={() => handleResendInvitation(invitation.invitation_code, invitation.email)}
                  className="p-2 text-gray-400 hover:text-[#80298F] hover:bg-[#80298F]/10 rounded-lg transition-colors"
                  title="Resend invitation"
                  data-testid={`resend-invitation-${invitation.invitation_code}`}
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleCancelInvitation(invitation.invitation_code, invitation.email)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Cancel invitation"
                  data-testid={`cancel-invitation-${invitation.invitation_code}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              Code: <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono">{invitation.invitation_code}</code>
            </span>
            <button
              onClick={() => copyInvitationCode(invitation.invitation_code)}
              className="text-[#80298F] hover:text-[#6B2278] flex items-center gap-1"
              title="Copy code"
            >
              <Copy className="h-3 w-3" />
              Copy
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
            <span className="truncate">By: {invitation.invited_by_name || invitation.invited_by}</span>
            <span className="hidden sm:inline">•</span>
            <span>{formatDate(invitation.created_at)}</span>
            <span>→</span>
            <span>{formatDate(invitation.expires_at)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Sort cases based on selected sort order
  const sortCases = (casesToSort) => {
    return [...casesToSort].sort((a, b) => {
      switch (caseSortOrder) {
        case 'name_asc':
          return (a.case_name || '').localeCompare(b.case_name || '');
        case 'name_desc':
          return (b.case_name || '').localeCompare(a.case_name || '');
        case 'date_asc':
          return new Date(a.updated_at || a.created_at || 0) - new Date(b.updated_at || b.created_at || 0);
        case 'date_desc':
          return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
        default:
          return 0;
      }
    });
  };

  const sortedCases = sortCases(filteredCases);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />

      {/* Action Buttons Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => setShowCaseModal(true)}
              className="bg-[#80298F] hover:bg-[#6B2278]"
              data-testid="create-case-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Case
            </Button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-gray-900" data-testid="staff-heading">Staff Dashboard</h1>
          <p className="text-gray-600 mt-1">Manage cases and clients</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <p className="text-sm text-gray-600">Total Cases</p>
            <p className="text-3xl font-bold text-[#80298F] mt-2">{cases.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <p className="text-sm text-gray-600">Total Clients</p>
            <p className="text-3xl font-bold text-[#FAA519] mt-2">{clients.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <p className="text-sm text-gray-600">Active This Month</p>
            <p className="text-3xl font-bold text-[#DC3239] mt-2">
              {cases.filter(c => c.current_status !== 'case_closed').length}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="overview-tab"
            >
              Cases Overview
            </button>
            <button
              onClick={() => setActiveTab('clients')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'clients'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="clients-tab"
            >
              Registered Clients ({clients.length})
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'invitations'
                  ? 'border-[#80298F] text-[#80298F]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="invitations-tab"
            >
              Invitations ({invitations.length})
            </button>
          </nav>
        </div>

        {/* Cases Overview Tab */}
        {activeTab === 'overview' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="cases-list">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-heading font-bold text-gray-900">All Cases</h2>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showArchivedCases}
                  onChange={(e) => setShowArchivedCases(e.target.checked)}
                  className="rounded border-gray-300"
                  data-testid="show-archived-checkbox"
                />
                Show archived
              </label>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Select value={caseSortOrder} onValueChange={setCaseSortOrder}>
                <SelectTrigger className="w-full sm:w-48" data-testid="case-sort-select">
                  <ArrowUpDown className="h-4 w-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name_asc">Name (A to Z)</SelectItem>
                  <SelectItem value="name_desc">Name (Z to A)</SelectItem>
                  <SelectItem value="date_desc">Updated (Latest first)</SelectItem>
                  <SelectItem value="date_asc">Updated (Earliest first)</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search cases..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="case-search-input"
                />
              </div>
            </div>
          </div>
          {(() => {
            const displayCases = sortCases(showArchivedCases 
              ? filteredCases 
              : filteredCases.filter(c => !c.is_archived));
            const archivedCount = cases.filter(c => c.is_archived).length;
            
            return cases.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No cases yet</p>
            ) : displayCases.length === 0 ? (
              <div className="text-center py-8">
                <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  {searchQuery ? `No cases match "${searchQuery}"` : 'No active cases'}
                </p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-[#80298F] hover:underline mt-2 text-sm"
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-500 mb-2">
                  Showing {displayCases.length} of {cases.length} cases
                  {archivedCount > 0 && !showArchivedCases && ` (${archivedCount} archived)`}
                </p>
                {displayCases.map((caseItem) => (
                  <div
                    key={caseItem.case_id}
                    className={`border rounded-lg p-4 hover:shadow-md transition-all ${
                      caseItem.is_archived 
                        ? 'border-gray-300 bg-gray-50' 
                        : 'border-gray-200 hover:border-[#80298F]'
                    }`}
                    data-testid={`case-${caseItem.case_id}`}
                  >
                    <div className="flex justify-between items-start">
                      <Link to={`/cases/${caseItem.case_id}`} className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className={`font-semibold ${caseItem.is_archived ? 'text-gray-500' : 'text-gray-900'}`}>
                            {caseItem.case_name}
                          </h3>
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 font-mono">
                            {caseItem.case_number}
                          </span>
                          {caseItem.is_archived && (
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-600">
                              Archived
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-gray-600">Client: {caseItem.client_name}</p>
                          {caseItem.client_registered === true && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700" title="Client has registered">
                              <UserCheck className="h-3 w-3" />
                              Registered
                            </span>
                          )}
                          {caseItem.client_registered === false && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" title="Client has not registered yet">
                              <UserX className="h-3 w-3" />
                              Pending
                            </span>
                          )}
                          {caseItem.client_registered === null && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500" title="No portal account linked to this client">
                              <UserX className="h-3 w-3" />
                              No Account
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                          <span>Type: {caseItem.case_type.replace('_', ' ')}</span>
                          <span>•</span>
                          <span>Updated: {formatDate(caseItem.status_updated_at)}</span>
                        </div>
                      </Link>
                      <div className="flex items-center gap-2 ml-4">
                        <span className="status-badge active text-xs">
                          {caseItem.current_status.replace(/_/g, ' ')}
                        </span>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleArchiveCase(caseItem.case_id, caseItem.case_name, caseItem.is_archived);
                          }}
                          className="p-2 text-gray-400 hover:text-[#80298F] hover:bg-[#80298F]/10 rounded-lg transition-colors"
                          title={caseItem.is_archived ? 'Unarchive case' : 'Archive case'}
                          data-testid={`archive-case-${caseItem.case_id}`}
                        >
                          {caseItem.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
        )}

        {/* Registered Clients Tab */}
        {activeTab === 'clients' && (
          <Card data-testid="clients-section">
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="h-5 w-5 text-[#80298F]" />
                  Registered Clients
                </CardTitle>
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search clients..."
                    value={clientSearchQuery}
                    onChange={(e) => setClientSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="client-search-input"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {clients.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No clients have registered yet</p>
                  <p className="text-sm text-gray-400 mt-1">Send invitations to clients to get started</p>
                </div>
              ) : (() => {
                const filteredClients = clients.filter(client => {
                  if (!clientSearchQuery.trim()) return true;
                  const query = clientSearchQuery.toLowerCase();
                  const clientCases = cases.filter(c => c.client_id === client.user_id);
                  return (
                    client.name?.toLowerCase().includes(query) ||
                    client.email?.toLowerCase().includes(query) ||
                    clientCases.some(c => c.case_name?.toLowerCase().includes(query))
                  );
                });
                
                return filteredClients.length === 0 ? (
                  <div className="text-center py-8">
                    <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No clients match "{clientSearchQuery}"</p>
                    <button
                      onClick={() => setClientSearchQuery('')}
                      className="text-[#80298F] hover:underline mt-2 text-sm"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500 mb-4">
                      {clientSearchQuery ? `Showing ${filteredClients.length} of ${clients.length} clients` : `${clients.length} client${clients.length !== 1 ? 's' : ''} registered`}
                    </p>
                    {filteredClients.map((client) => {
                      const clientCases = cases.filter(c => c.client_id === client.user_id);
                      return (
                        <div
                          key={client.user_id}
                          className="border border-gray-200 rounded-lg p-4 hover:border-[#80298F]/30 transition-colors"
                          data-testid={`client-${client.user_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-4">
                              <div className="h-12 w-12 rounded-full bg-[#80298F]/10 flex items-center justify-center flex-shrink-0">
                                <span className="text-lg font-semibold text-[#80298F]">
                                  {client.name?.charAt(0)?.toUpperCase() || 'C'}
                                </span>
                              </div>
                              <div className="flex-1">
                                <h3 className="font-semibold text-gray-900">{client.name}</h3>
                                <span className="text-sm text-gray-600 flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {client.email}
                                </span>
                                {clientCases.length > 0 ? (
                                  <div className="mt-3">
                                    <p className="text-xs text-gray-500 mb-2">Associated Cases ({clientCases.length}):</p>
                                    <div className="space-y-2">
                                      {clientCases.map(caseItem => (
                                        <Link
                                          key={caseItem.case_id}
                                          to={`/cases/${caseItem.case_id}`}
                                          className="block text-sm bg-gray-50 rounded px-3 py-2 hover:bg-[#80298F]/5 transition-colors"
                                        >
                                          <div className="flex items-center justify-between">
                                            <span className="text-gray-800 font-medium truncate mr-2">{caseItem.case_name}</span>
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#FAA519]/20 text-[#80298F] whitespace-nowrap">
                                              {caseItem.current_status?.replace(/_/g, ' ')}
                                            </span>
                                          </div>
                                        </Link>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-400 mt-2">No cases assigned</p>
                                )}
                              </div>
                            </div>
                            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 flex items-center gap-1 flex-shrink-0">
                              <CheckCircle className="h-3 w-3" />
                              Active
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Invitations Tab */}
        {activeTab === 'invitations' && (
          <div className="space-y-4">
            {/* Search and Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative flex-1 w-full sm:max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search invitations by name, email, or code..."
                  value={invitationSearchQuery}
                  onChange={(e) => setInvitationSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="staff-invitation-search-input"
                />
              </div>
              <Button
                variant={showArchivedInvitations ? "default" : "outline"}
                size="sm"
                onClick={() => setShowArchivedInvitations(!showArchivedInvitations)}
                className={showArchivedInvitations ? "bg-[#80298F] hover:bg-[#6B2278]" : ""}
                data-testid="staff-toggle-archived-invitations"
              >
                {showArchivedInvitations ? <ArchiveRestore className="h-4 w-4 mr-1" /> : <Archive className="h-4 w-4 mr-1" />}
                {showArchivedInvitations ? "Show Active" : "Show Archived"}
              </Button>
            </div>
            
            {/* Archived indicator */}
            {showArchivedInvitations && (
              <div className="bg-gray-100 border border-gray-200 rounded-lg p-3 flex items-center gap-2">
                <Archive className="h-5 w-5 text-gray-500" />
                <span className="text-sm text-gray-600">Showing archived invitations ({archivedInvitations.length} total)</span>
              </div>
            )}
            
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-3 sm:pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Pending</p>
                      <p className="text-xl sm:text-3xl font-bold text-amber-600 mt-1">
                        {getPendingInvitations().length}
                      </p>
                    </div>
                    <Clock className="h-6 w-6 sm:h-10 sm:w-10 text-amber-600/20" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 sm:pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Accepted</p>
                      <p className="text-xl sm:text-3xl font-bold text-green-600 mt-1">
                        {getAcceptedInvitations().length}
                      </p>
                    </div>
                    <CheckCircle className="h-6 w-6 sm:h-10 sm:w-10 text-green-600/20" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 sm:pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Expired</p>
                      <p className="text-xl sm:text-3xl font-bold text-red-600 mt-1">
                        {getExpiredInvitations().length}
                      </p>
                    </div>
                    <XCircle className="h-6 w-6 sm:h-10 sm:w-10 text-red-600/20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Invitation Groups */}
            {displayInvitations.length === 0 ? (
              <Card>
                <CardContent className="px-4 sm:px-6 py-8">
                  <div className="text-center">
                    <Mail className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">{showArchivedInvitations ? 'No archived invitations' : 'No invitations sent yet'}</p>
                    <p className="text-sm text-gray-400 mt-1">{showArchivedInvitations ? 'Archived invitations will appear here' : 'Create a new case to send a client invitation'}</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Pending Invitations */}
                {getPendingInvitations().length > 0 && (
                  <Card>
                    <button
                      onClick={() => toggleInvitationGroup('pending')}
                      className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      data-testid="toggle-pending-invitations"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-100 rounded-lg">
                          <Clock className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-gray-900">Pending</h3>
                          <p className="text-sm text-gray-500">{getPendingInvitations().length} invitation{getPendingInvitations().length !== 1 ? 's' : ''} awaiting response</p>
                        </div>
                      </div>
                      {expandedInvitationGroups.pending ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    {expandedInvitationGroups.pending && (
                      <CardContent className="px-4 sm:px-6 pt-0 pb-4">
                        <div className="space-y-3">
                          {getPendingInvitations().map((invitation) => (
                            <InvitationCard key={invitation.invitation_code} invitation={invitation} />
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                )}

                {/* Accepted Invitations */}
                {getAcceptedInvitations().length > 0 && (
                  <Card>
                    <button
                      onClick={() => toggleInvitationGroup('accepted')}
                      className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      data-testid="toggle-accepted-invitations"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-gray-900">Accepted</h3>
                          <p className="text-sm text-gray-500">{getAcceptedInvitations().length} invitation{getAcceptedInvitations().length !== 1 ? 's' : ''} accepted</p>
                        </div>
                      </div>
                      {expandedInvitationGroups.accepted ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    {expandedInvitationGroups.accepted && (
                      <CardContent className="px-4 sm:px-6 pt-0 pb-4">
                        <div className="space-y-3">
                          {getAcceptedInvitations().map((invitation) => (
                            <InvitationCard key={invitation.invitation_code} invitation={invitation} />
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                )}

                {/* Expired Invitations */}
                {getExpiredInvitations().length > 0 && (
                  <Card>
                    <button
                      onClick={() => toggleInvitationGroup('expired')}
                      className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      data-testid="toggle-expired-invitations"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <XCircle className="h-5 w-5 text-red-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-gray-900">Expired</h3>
                          <p className="text-sm text-gray-500">{getExpiredInvitations().length} invitation{getExpiredInvitations().length !== 1 ? 's' : ''} expired</p>
                        </div>
                      </div>
                      {expandedInvitationGroups.expired ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    {expandedInvitationGroups.expired && (
                      <CardContent className="px-4 sm:px-6 pt-0 pb-4">
                        <div className="space-y-3">
                          {getExpiredInvitations().map((invitation) => (
                            <InvitationCard key={invitation.invitation_code} invitation={invitation} />
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Create Case + Invite Client Modal */}
      <Dialog open={showCaseModal} onOpenChange={setShowCaseModal}>
        <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="case-modal">
          <DialogHeader>
            <DialogTitle>Create New Case + Invite Client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCase} className="space-y-4">
            {/* Case Details Section */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm border-b pb-2">Case Details</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case Name *</label>
                <Input
                  value={newCase.case_name}
                  onChange={(e) => setNewCase({...newCase, case_name: e.target.value})}
                  placeholder="Smith v. Johnson"
                  required
                  data-testid="case-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case ID *</label>
                <Input
                  value={newCase.case_id_custom}
                  onChange={(e) => setNewCase({...newCase, case_id_custom: e.target.value})}
                  placeholder="e.g., 2026-PI-001"
                  required
                  data-testid="case-id-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case Type *</label>
                <Select value={newCase.case_type} onValueChange={(val) => setNewCase({...newCase, case_type: val})}>
                  <SelectTrigger data-testid="case-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal_injury">Personal Injury</SelectItem>
                    <SelectItem value="employment">Employment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Status *</label>
                <Select value={newCase.current_status} onValueChange={(val) => setNewCase({...newCase, current_status: val})}>
                  <SelectTrigger data-testid="case-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {caseStatusOptions.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Milestones <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <div className="border rounded-md p-3 max-h-40 overflow-y-auto bg-gray-50">
                  {milestoneOptions.length === 0 ? (
                    <p className="text-sm text-gray-500">No milestones configured</p>
                  ) : (
                    <div className="space-y-2">
                      {milestoneOptions.map((milestone) => (
                        <label key={milestone.value} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                          <input
                            type="checkbox"
                            checked={newCase.milestones.includes(milestone.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewCase({...newCase, milestones: [...newCase.milestones, milestone.value]});
                              } else {
                                setNewCase({...newCase, milestones: newCase.milestones.filter(m => m !== milestone.value)});
                              }
                            }}
                            className="h-4 w-4 text-[#80298F] border-gray-300 rounded focus:ring-[#80298F]"
                            data-testid={`milestone-checkbox-${milestone.value}`}
                          />
                          <span className="text-sm text-gray-700">{milestone.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {newCase.milestones.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{newCase.milestones.length} milestone(s) selected</p>
                )}
              </div>
            </div>

            {/* Client Details Section */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm border-b pb-2">Client Details</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                <Input
                  value={newCase.client_name}
                  onChange={(e) => setNewCase({...newCase, client_name: e.target.value})}
                  placeholder="John Smith"
                  required
                  data-testid="client-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Email *</label>
                <Input
                  type="email"
                  value={newCase.client_email}
                  onChange={(e) => setNewCase({...newCase, client_email: e.target.value})}
                  placeholder="client@example.com"
                  required
                  data-testid="client-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Phone *</label>
                <Input
                  type="tel"
                  value={formatPhoneNumber(newCase.client_phone)}
                  onChange={(e) => setNewCase({...newCase, client_phone: formatPhoneInput(e.target.value)})}
                  placeholder="(310) 567-7777"
                  required
                  data-testid="client-phone-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#80298F] hover:bg-[#6B2278]" 
              data-testid="create-case-submit-button"
              disabled={creatingCase}
            >
              {creatingCase ? 'Creating...' : 'Create Case & Send Invitation'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default StaffDashboard;